import { useEffect, useState, type ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import type { AppointmentFavorite, AppointmentFavoritesResponse, GownPickerCatalog, GownPickerItem } from '@fitting-room/contracts'
import { Lock, Search, Undo2, X } from 'lucide-react'
import { assetUrl } from '../../lib/api'
import { useGownPicker, useSaveAppointmentFavorites } from '../../lib/adminQueries'
import {
  CUSTOMER_PICK_REASON,
  EMPTY_DRAFT,
  addedByLabel,
  canRemove,
  gownDetail,
  isDirty,
  isOnList,
  keep,
  saveRequest,
  slotRoom,
  takeOff,
  toggleGown,
  type FavoritesDraft,
} from '../../lib/favorites'
import { useStaffToken } from '../../lib/staffToken'
import { Chip } from './Chip'

const CATALOGS: ReadonlyArray<readonly [GownPickerCatalog, string]> = [
  ['made_to_order', 'Special order'],
  ['off_the_rack', 'Off the rack'],
]

/**
 * "Her favorites", edited: a large dialog centered over the drawer, the
 * catalog on the left and the customer's list on the right. Ported from
 * tperent's `DressPickerDialog` — the big search field that takes the focus,
 * the filter chips under it, the live count, a grid of photo cards that
 * toggle on a click, pages of 24, and a footer that says how many are picked
 * beside Cancel and the one button that commits — restyled to the console,
 * with tperent's tally replaced by the list itself, so a stylist sees what
 * the customer already has before adding to it.
 *
 * Nothing is sent until Save, which sends the whole sitting as one request;
 * Cancel, Escape and the overlay throw it away. The body is mounted only
 * while open, as tperent's is, so every opening starts clean.
 *
 * On a phone it fills the screen, and the two halves become two tabs.
 */
export function FavoritesPicker({
  open,
  onClose,
  visitId,
  customerName,
  favorites,
}: {
  readonly open: boolean
  readonly onClose: () => void
  readonly visitId: string
  readonly customerName: string | undefined
  readonly favorites: AppointmentFavoritesResponse
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay className="st-overlay" />
        <Dialog.Content className="st-picker st-root" aria-describedby={undefined}>
          {open ? <PickerBody onClose={onClose} visitId={visitId} customerName={customerName} favorites={favorites} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function PickerBody({
  onClose,
  visitId,
  customerName,
  favorites,
}: {
  readonly onClose: () => void
  readonly visitId: string
  readonly customerName: string | undefined
  readonly favorites: AppointmentFavoritesResponse
}) {
  const { token } = useStaffToken()
  const [catalog, setCatalog] = useState<GownPickerCatalog>('made_to_order')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [draft, setDraft] = useState<FavoritesDraft>(EMPTY_DRAFT)
  const [pane, setPane] = useState<'catalog' | 'list'>('catalog')
  const debouncedQ = useDebounced(q.trim(), 250)
  const gowns = useGownPicker(token, { catalog, q: debouncedQ, page }, true)
  const save = useSaveAppointmentFavorites(token, visitId)

  const saved = favorites.favorites
  const room = favorites.slot ? slotRoom(favorites.slot, saved, draft) : undefined
  const onList = (id: string) => isOnList(draft, saved, id)
  const listCount = saved.length - draft.removed.size + draft.added.length

  return (
    <>
      <header className="st-picker-head">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="st-eyebrow">{customerName ?? 'Her favorites'}</div>
            <Dialog.Title className="st-display mt-1 text-2xl">Edit favorites</Dialog.Title>
          </div>
          <Dialog.Close asChild>
            <button type="button" className="st-iconbtn" aria-label="Close">
              <X size={16} aria-hidden />
            </button>
          </Dialog.Close>
        </div>
        <div className="st-picker-tabs mt-3" role="tablist" aria-label="Show">
          <button type="button" role="tab" aria-selected={pane === 'catalog'} onClick={() => setPane('catalog')}>
            Catalog
          </button>
          <button type="button" role="tab" aria-selected={pane === 'list'} onClick={() => setPane('list')}>
            Her favorites <span className="tabular-nums">({listCount})</span>
          </button>
        </div>
      </header>

      <div className="st-picker-body" data-pane={pane}>
        <section className="st-picker-catalog" aria-label="Catalog">
          <div className="st-picker-search">
            <label className="relative block">
              <Search size={16} aria-hidden className="st-meta pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="search"
                autoFocus
                className="st-input st-input-search w-full text-base"
                placeholder="Style # or name"
                aria-label="Search gowns"
                value={q}
                onChange={(event) => {
                  setQ(event.target.value)
                  setPage(1)
                }}
              />
            </label>
            <div className="mt-2.5 flex flex-wrap gap-1.5" role="group" aria-label="Catalog">
              {CATALOGS.map(([key, label]) => (
                <Chip
                  key={key}
                  pressed={catalog === key}
                  onClick={() => {
                    setCatalog(key)
                    setPage(1)
                  }}
                >
                  {label}
                </Chip>
              ))}
            </div>
            <p className="st-meta mt-2" aria-live="polite">
              {gowns.isError
                ? 'Couldn’t load the catalog. Try again.'
                : !gowns.data
                  ? 'Loading…'
                  : gowns.data.items.length === 0
                    ? debouncedQ
                      ? `Nothing matches “${debouncedQ}”.`
                      : catalog === 'off_the_rack'
                        ? 'Nothing on the rack right now.'
                        : 'No gowns in the catalog.'
                    : `Page ${gowns.data.page} · click a gown to add it`}
            </p>
          </div>

          <div className="st-picker-scroll">
            <ul className="st-picker-grid" aria-busy={gowns.isFetching}>
              {(gowns.data?.items ?? []).map((gown) => {
                const added = draft.added.some((item) => item.garmentId === gown.garmentId)
                const already = !added && onList(gown.garmentId)
                const blocked = !added && !already && room?.full === true
                return (
                  <li key={gown.garmentId}>
                    <button
                      type="button"
                      className="st-picker-card"
                      aria-pressed={added}
                      disabled={already || blocked}
                      title={blocked ? room?.fullMessage : already ? 'Already on the list' : undefined}
                      onClick={() => setDraft((current) => toggleGown(current, saved, gown))}
                      data-garment-id={gown.garmentId}
                    >
                      <GownPhoto url={gown.imageUrl} className="st-picker-photo" />
                      <span className="block truncate text-sm font-medium tabular-nums">{gown.code ?? gown.name}</span>
                      <span className="st-meta block truncate">{pickerDetail(gown)}</span>
                      {already ? <span className="st-picker-flag">On the list</span> : added ? <span className="st-picker-flag">Adding</span> : null}
                    </button>
                  </li>
                )
              })}
            </ul>
            {gowns.data && (page > 1 || gowns.data.hasMore) ? (
              <div className="mt-4 flex items-center justify-center gap-3">
                <button type="button" className="st-btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </button>
                <span className="st-meta tabular-nums">Page {page}</span>
                <button type="button" className="st-btn-ghost" disabled={!gowns.data.hasMore} onClick={() => setPage((p) => p + 1)}>
                  Next
                </button>
              </div>
            ) : null}
          </div>
        </section>

        <aside className="st-picker-list" aria-label="Her favorites">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h3 className="st-eyebrow">Her favorites</h3>
            {room ? <span className="st-meta tabular-nums">{room.summary}</span> : null}
          </div>
          {room?.fullMessage ? (
            <p className="st-notice mb-2 text-xs" role="status">
              {room.fullMessage}
            </p>
          ) : null}

          {draft.added.length > 0 ? (
            <>
              <h4 className="st-meta mb-1 mt-1 font-medium">Adding</h4>
              <ul className="mb-3 space-y-1.5" aria-label="Adding">
                {draft.added.map((item) => (
                  <ListRow
                    key={item.garmentId}
                    gown={item}
                    note={favorites.slot ? `To ${favorites.slot.label}` : undefined}
                    action={
                      <button
                        type="button"
                        className="st-iconbtn"
                        aria-label={`Remove ${item.code ?? item.name}`}
                        onClick={() => setDraft((current) => takeOff(current, saved, item.garmentId))}
                      >
                        <X size={14} aria-hidden />
                      </button>
                    }
                  />
                ))}
              </ul>
            </>
          ) : null}

          {saved.length === 0 && draft.added.length === 0 ? (
            <p className="st-meta">Nothing on it yet. Click a gown to add it.</p>
          ) : null}
          {saved.length > 0 ? (
            <ul className="space-y-1.5" aria-label="On the list">
              {saved.map((favorite) => (
                <SavedRow
                  key={favorite.garmentId}
                  favorite={favorite}
                  removed={draft.removed.has(favorite.garmentId)}
                  onTakeOff={() => setDraft((current) => takeOff(current, saved, favorite.garmentId))}
                  onKeep={() => setDraft((current) => keep(current, favorite.garmentId))}
                />
              ))}
            </ul>
          ) : null}
        </aside>
      </div>

      <footer className="st-picker-foot">
        <span className="st-meta text-sm" aria-live="polite">
          {summaryOf(draft)}
        </span>
        <div className="flex flex-col items-end gap-1">
          {save.isError ? (
            <p className="text-xs text-[color:#8d3f2c]" role="alert">
              {save.error.message}
            </p>
          ) : null}
          <div className="flex gap-2">
            <button type="button" className="st-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="st-btn"
              disabled={!isDirty(draft) || save.isPending}
              onClick={() => save.mutate(saveRequest(draft), { onSuccess: onClose })}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </footer>
    </>
  )
}

/** One saved gown on the right: the customer's with a lock that says why, a stylist's with the way to take it off. */
function SavedRow({
  favorite,
  removed,
  onTakeOff,
  onKeep,
}: {
  readonly favorite: AppointmentFavorite
  readonly removed: boolean
  readonly onTakeOff: () => void
  readonly onKeep: () => void
}) {
  const label = favorite.code ?? favorite.name
  const action = !canRemove(favorite) ? (
    <span className="st-meta inline-flex h-8 w-8 items-center justify-center" title={CUSTOMER_PICK_REASON} aria-label={CUSTOMER_PICK_REASON} role="img">
      <Lock size={14} aria-hidden />
    </span>
  ) : removed ? (
    <button type="button" className="st-iconbtn" aria-label={`Keep ${label}`} title="Keep it" onClick={onKeep}>
      <Undo2 size={14} aria-hidden />
    </button>
  ) : (
    <button type="button" className="st-iconbtn" aria-label={`Remove ${label}`} onClick={onTakeOff}>
      <X size={14} aria-hidden />
    </button>
  )
  return <ListRow gown={favorite} note={[favorite.slotLabel, removed ? 'Coming off' : addedByLabel(favorite)].filter(Boolean).join(' · ')} removed={removed} action={action} />
}

function ListRow({
  gown,
  note,
  action,
  removed = false,
}: {
  readonly gown: { garmentId: string; code?: string | undefined; name: string; imageUrl?: string | undefined; color?: string | undefined; offTheRack: boolean }
  readonly note: string | undefined
  readonly action: ReactNode
  readonly removed?: boolean
}) {
  return (
    <li className="flex items-center gap-2.5 text-sm" data-garment-id={gown.garmentId} data-removed={removed || undefined}>
      <GownPhoto url={gown.imageUrl} className="st-thumb" />
      <span className={removed ? 'min-w-0 flex-1 line-through opacity-60' : 'min-w-0 flex-1'}>
        <span className="block truncate font-medium tabular-nums">{gown.code ?? gown.name}</span>
        <span className="st-meta block truncate">{gownDetail(gown)}</span>
        {note ? <span className="st-meta block truncate">{note}</span> : null}
      </span>
      {action}
    </li>
  )
}

/** A photo if there is one; the thumbnail's own ground if not — never the browser's broken-image icon. */
function GownPhoto({ url, className }: { readonly url: string | undefined; readonly className: string }) {
  const [failed, setFailed] = useState(false)
  if (!url || failed) return <span className={className} aria-hidden />
  return <img className={className} src={assetUrl(url)} alt="" loading="lazy" onError={() => setFailed(true)} />
}

/** A card's line under the style #: the drawer's detail, and for a rail card the sizes on the rack. */
function pickerDetail(gown: GownPickerItem): string {
  const sizes = gown.sizes.length > 0 ? `Sizes ${gown.sizes.join(', ')}` : undefined
  return [gownDetail(gown) || undefined, sizes].filter(Boolean).join(' · ') || ' '
}

function summaryOf(draft: FavoritesDraft): string {
  const parts = [
    draft.added.length > 0 ? `Adding ${draft.added.length}` : undefined,
    draft.removed.size > 0 ? `taking off ${draft.removed.size}` : undefined,
  ].filter(Boolean)
  if (parts.length === 0) return 'No changes yet'
  const sentence = parts.join(', ')
  return sentence.charAt(0).toUpperCase() + sentence.slice(1)
}

function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return settled
}
